import {DokumentId, DokumentSignatarModell, DokumentType, DokumentVersjonModell, GruppeMedlemListeModell, PotensiellDokumentSignatarModell, SaksdokumentModell, Signeringsmetode, SigneringStatus, TilgangsrolleMedlem, TynnSakModell} from "../tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {SkjermetEntitetType} from "felles-ng2/tjenester/datalager/SkjermingDatatjeneste";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {erIkkeSkjermet, erSkjermet, Skjerming, skjermingsinformasjonFor} from "felles-ng2/modell/skjermingModellHjelpere";
import {Brukerkontekst} from "felles-ng2/tjenester/Brukerkontekst";
import {Observable, of} from "rxjs";
import {map} from "rxjs/operators";

const debug = initDebugger('dokumentModellHjelpere');

const genererteDokumentTyper = [DokumentType.protokolldokument, DokumentType.protokolldokumentISak, DokumentType.generertAgendaDokument, DokumentType.generertForfalteOppgaverDokument, DokumentType.generertSpørreundersøkelseResultaterDokument];

export function erSaksdokument(dokumentversjon: DokumentVersjonModell): boolean {
    return erIkkeNil(dokumentversjon.foreldre.sakId);
}

export function erArkivdokument(dokumentversjon: DokumentVersjonModell): boolean {
    return erIkkeNil(dokumentversjon.foreldre.arkivfilnodeId);
    // @todo skulle denne vært med? foreløpig har vi ikke noe arkivdokumentlager så vet egentlig ikke enda.. erIkkeNil(dokumentversjon.foreldre.arkivdokumentId);
}

export function erProtokolldokumentISak(dokumentversjon: DokumentVersjonModell): boolean {
    return dokumentversjon.type == DokumentType.protokolldokumentISak;
}

export function erProtokolldokumentISakEllerProtokoll(dokumentversjon: DokumentVersjonModell): boolean {
    return [DokumentType.protokolldokument, DokumentType.protokolldokumentISak].includes(dokumentversjon.type);
}

export function erArkivdokumentdokumentISak(dokumentversjon: DokumentVersjonModell): boolean {
    return dokumentversjon.type == DokumentType.arkivdokumentISak;
}

export function erGenerertDokument(dokumentversjon: DokumentVersjonModell): boolean {
    return genererteDokumentTyper.includes(dokumentversjon.type);
}

/** Sorterer saksdokumenter etter {@link SaksdokumentModell#indeks} */
export function sorterSaksdokumenter(dokumenter: SaksdokumentModell[]) {
    return dokumenter.sort(sorterSaksdokument);
}

/** Sorterer saksdokumenter etter {@link SaksdokumentModell#indeks} */
export function sorterSaksdokument(a: SaksdokumentModell, b: SaksdokumentModell): number {
    return a.indeks - b.indeks;
}

/** Returnerer informasjon om antall dokumenter lest */
export function antallDokumenterLest(dokumenter: SaksdokumentModell[]): DokumenterInformasjon {
    return {
        antallTotalt: dokumenter.length,
        antallUleste: dokumenter.filter(dokument => !dokument.erLestAv).length
    };
}

/** Returnerer pdf-filnavn dersom pdf-representasjon finnes, eller originalt filnavn.
 * Samme som DokumentInformasjon.standardformat.filnavn */
export function standardDokumentNavn(dokumentversjon: DokumentVersjonModell): string {
    return dokumentversjon.harPdfRepresentasjon ?
      dokumentversjon.pdfFilnavn! :
      dokumentversjon.filnavn;
}

export function erDokumentSkjermet(dokumentversjon: DokumentVersjonModell): boolean {
    return erSkjermet(dokumentversjon);
}

export function erDokumentIkkeSkjermet(dokumentversjon: DokumentVersjonModell): boolean {
    return erIkkeSkjermet(dokumentversjon);
}

export function dokumentSkjermingsinformasjonFor(dokumentversjon: DokumentVersjonModell, gruppemedlemmer: GruppeMedlemListeModell[]): Skjerming {
    return skjermingsinformasjonFor(dokumentversjon, gruppemedlemmer);
}

const brukerSkalSignereStatuser = [SigneringStatus.delvisSignert, SigneringStatus.tilSignering];

export type Signatarinformasjon = {
    skalSignere: boolean;
    signatar: DokumentSignatarModell | null;
    antattStoettedeSigneringsmetoder: Signeringsmetode[];
}

/**
 * Bygger og returnerer {@link Signatarinformasjon} basert på angitt dokumentversjon.
 * Bruker signatarinformasjon-funksjonen for å hente signatarinformasjon fra server, dersom angitt dokumentversjon krever signering.
 */
export function signatarinformasjon(dokumentversjon: DokumentVersjonModell, signatarinformasjon: (dokumentId: DokumentId) => Observable<PotensiellDokumentSignatarModell>): Observable<Signatarinformasjon> {
    //spesialhåndtering av protokolldokumentISak - må hente data fra server for å kunne avgjøre om bruker skal signere
    if (dokumentversjon.type == DokumentType.protokolldokumentISak)
        return signatarinformasjon(dokumentversjon.foreldre.protokolldokumentId!).pipe(
          map(modell => potensiellDokumentSignatarModellSomSignatarinformasjon(modell)))

    if (brukerSkalSignereStatuser.includes(dokumentversjon.signeringStatus))
        return signatarinformasjon(dokumentversjon.dokumentId).pipe(
          map(modell => potensiellDokumentSignatarModellSomSignatarinformasjon(modell)))

    return of(dokumentSkalIkkeSigneresAvBruker());
}

export function dokumentSkalIkkeSigneresAvBruker(): Signatarinformasjon {
    return {skalSignere: false, signatar: null, antattStoettedeSigneringsmetoder: []};
}

function potensiellDokumentSignatarModellSomSignatarinformasjon(signatarModell: PotensiellDokumentSignatarModell): Signatarinformasjon {
    return {
        skalSignere: signatarModell.signatarFunnet && !signatarModell.signatar!.harSignert,
        signatar: signatarModell.signatar ?? null,
        antattStoettedeSigneringsmetoder: signatarModell.antattStoettedeSigneringsmetoder
    }
}

export function erSkjermbartSaksdokument(dokumentversjon: DokumentVersjonModell): boolean {
    return erIkkeNil(skjermetEntitetTypeForDokument(dokumentversjon));
}

export function skjermetEntitetTypeForDokument(dokumentversjon: DokumentVersjonModell): SkjermetEntitetType | null {
    if ([DokumentType.opplastetSaksdokument, DokumentType.arkivdokumentISak, DokumentType.generertAgendaDokument, DokumentType.generiskDokument]
      .includes(dokumentversjon.type))
        return SkjermetEntitetType.saksdokument;

    if (dokumentversjon.type == DokumentType.arkivdokument)
        return SkjermetEntitetType.arkivdokument;

    return null;
}

/** Sann dersom bruker kan redigere dokumentet */
export function kanSaksdokumentRedigeres(dokumentversjon: DokumentVersjonModell, tilknyttetSak: TynnSakModell, kontekst: Brukerkontekst): boolean {
    //burde man her heller tatt inn møte og ikke sak - i tråd med konseptet om å ikke navigere graf som krysser data-lager-grenser (sak og møte)
    if (tilknyttetSak.moete.laastForRedigering) return false;

    //sjekk rolle-basert tilgang
    if (kontekst.ikkeInneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, dokumentversjon.gruppeId)) return false;

    return true;
}


//hjelpemetoder

export type DokumenterInformasjon = { antallTotalt: number, antallUleste: number };

